import { useState, useRef } from "react";
import { Upload, Link, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface ImageUploadProps {
  image: string | null;
  onImageChange: (image: string | null) => void;
  maxSizeMB?: number;
  acceptedFormats?: string[];
  disabled?: boolean;
  /** Se fornita, il file viene caricato su storage e onImageChange riceve il public URL */
  uploadFn?: (file: File) => Promise<string>;
  /** Override the height of the upload/preview container (default: "h-[350px]") */
  heightClass?: string;
}

const ImageUpload = ({ 
  image, 
  onImageChange, 
  maxSizeMB = 10,
  acceptedFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
  disabled = false,
  uploadFn,
  heightClass = 'h-[350px]',
}: ImageUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (disabled || !files || files.length === 0) return;

    const file = files[0]; // Take only the first file

    // Validate file
    if (!acceptedFormats.includes(file.type)) {
      alert(`Invalid file format. Please use: ${acceptedFormats.join(', ')}`);
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    if (uploadFn) {
      // Upload su storage e ritorna il public URL
      setIsLoading(true);
      uploadFn(file)
        .then((url) => onImageChange(url))
        .catch((err) => {
          console.error('Storage upload error:', err);
          alert('Upload failed. Please try again.');
        })
        .finally(() => setIsLoading(false));
      return;
    }

    // Fallback: converti in base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        console.log('Image loaded successfully:', result.substring(0, 50) + '...');
        onImageChange(result);
      }
    };
    reader.onerror = (e) => {
      console.error('Error reading file:', e);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleUrlSubmit = async () => {
    if (!imageUrl.trim() || disabled) return;

    setIsLoading(true);
    try {
      // Validate URL format
      const url = new URL(imageUrl);
      
      // Check if it's a valid image URL
      const response = await fetch(url, { method: 'HEAD' });
      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.startsWith('image/')) {
        alert('The URL does not point to a valid image');
        return;
      }

      // Set the image URL directly
      onImageChange(imageUrl);
      setImageUrl("");
      setShowUrlInput(false);
    } catch (error) {
      console.error('Error loading image from URL:', error);
      alert('Invalid URL or image could not be loaded');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUrlSubmit();
    }
  };

  const handleUrlCancel = () => {
    setImageUrl("");
    setShowUrlInput(false);
  };

  return (
    <div className="space-y-6">
      {/* Upload Area - Always visible with image overlay */}
      <div className={`relative ${heightClass}`}>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors h-full flex flex-col justify-center items-center ${
            disabled
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
              : isDragOver
              ? 'border-blue-400 bg-blue-50 cursor-pointer'
              : 'border-gray-300 hover:border-gray-400 cursor-pointer'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600 mb-2">Drag and drop an image here, or click to select</p>
          <p className="text-sm text-gray-500 mb-4">
            {acceptedFormats.join(', ').toUpperCase()} up to {maxSizeMB}MB
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(',')}
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>

        {/* Image Preview - Absolute positioned over upload area */}
        {image && (
          <div 
            className={`absolute inset-0 flex items-center justify-center bg-white bg-opacity-95 rounded-lg group ${
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            onClick={handleClick}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="rounded-lg overflow-hidden bg-gray-100 border border-gray-200 shadow-lg max-w-full max-h-full">
                <img
                  src={image}
                  alt="Uploaded image - Click to change"
                  className="max-h-full max-w-full h-auto w-auto object-contain"
                  style={{ maxHeight: 'calc(100% - 2rem)' }}
                  onError={(e) => {
                    console.error('Error loading image:', e);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              
              {/* Hover overlay with upload icon - Only show if not disabled */}
              {!disabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="flex flex-col items-center justify-center text-white">
                    <Upload className="h-8 w-8 mb-2" />
                    <span className="text-sm font-medium">Click to change</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add from URL button */}
      {!showUrlInput && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowUrlInput(true)}
            disabled={disabled}
            className="flex items-center gap-2"
          >
            <Link className="h-4 w-4" />
            Add from URL
          </Button>
        </div>
      )}

      {/* URL Input Section */}
      {showUrlInput && (
        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2">
            <Link className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Add image from URL</span>
          </div>
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={disabled || isLoading}
              className="flex-1"
            />
            <Button
              type="button"
              onClick={handleUrlSubmit}
              disabled={disabled || isLoading || !imageUrl.trim()}
              size="sm"
            >
              {isLoading ? "Loading..." : "Add"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleUrlCancel}
              disabled={disabled || isLoading}
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

    </div>
  );
};

export default ImageUpload;
